import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, languages } from '../i18n/translations';
import { LANGS, DEFAULT_LANG } from '../utils/seoUrls';

const LanguageContext = createContext(null);

/**
 * LanguageProvider — Phase 2 SEO:
 * The URL is the source of truth for the active language. App.js detects the
 * lang from window.location.pathname at boot and passes it via `initialLang`.
 * Changing the language triggers a full-page navigation to the new /{lang}/
 * URL so Googlebot, the prerender layer, and the BrowserRouter basename all
 * stay in sync.
 */
// Single slash convention: /en → /en/ (matches canonicals, sitemap, hreflang)  [trailing-slash]
if (typeof window !== 'undefined' && /^\/(en|de|lb)$/.test(window.location.pathname)) {
  window.history.replaceState(null, '', `${window.location.pathname}/${window.location.search}${window.location.hash}`);
}

export const LanguageProvider = ({ children, initialLang }) => {
  const [language, setLanguageState] = useState(() => {
    if (initialLang && translations[initialLang]) return initialLang;
    // Fallback for environments where App.js didn't pass initialLang.
    const saved = localStorage.getItem('lebonlux_language');
    if (saved && translations[saved]) return saved;
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) return browserLang;
    return DEFAULT_LANG;
  });

  useEffect(() => {
    localStorage.setItem('lebonlux_language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Switch language by rewriting the URL. fr (default) has no prefix; other
  // languages live under /{lang}/...  We use window.location.assign for a
  // full reload so the BrowserRouter basename is re-detected from the URL.
  const setLanguage = useCallback((newLang) => {
    if (!translations[newLang] || newLang === language) {
      setLanguageState(newLang);
      return;
    }
    const currentPath = window.location.pathname;
    const parts = currentPath.split('/');
    // Strip current lang prefix if present
    let rest = currentPath;
    if (LANGS.includes(parts[1])) {
      rest = '/' + parts.slice(2).join('/');
    }
    if (!rest.startsWith('/')) rest = '/' + rest;
    const target =
      newLang === DEFAULT_LANG
        ? rest + window.location.search + window.location.hash
        : `/${newLang}${rest === '/' ? '/' : rest}` +
          window.location.search +
          window.location.hash;
    // Persist preference before the page reloads.
    localStorage.setItem('lebonlux_language', newLang);
    window.location.assign(target);
  }, [language]);

  // Translation function with interpolation support
  const t = useCallback((key, params = {}) => {
    let text = translations[language]?.[key] || translations['en']?.[key] || key;
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
    });
    return text;
  }, [language]);

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      languages,
      currentLanguage,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
